<script setup>
import AppLayout from "@/Layouts/AppLayout.vue";
import InputError from "@/Components/InputError.vue";
import InputLabel from "@/Components/InputLabel.vue";
import FormSection from "@/Components/FormSection.vue";
import TextInput from "@/Components/TextInput.vue";
import { useForm } from "@inertiajs/inertia-vue3";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import { ref, watch, computed } from "vue";
import SampleDatabaseUpload from "@/Components/Common/Media/SampleDatabaseUpload.vue";
import '@vuepic/vue-datepicker/dist/main.css'
import { Inertia } from "@inertiajs/inertia";
import SearchSelect from "@/Components/SearchSelect.vue";
import SelectInput from "@/Components/SelectInput.vue";

const props = defineProps({
  sample: Object,
  vendors: Array,
  clients: Array,
  sampleTypes: Array,
  garments: Array,
  conveyors: Object,
  locations: Array,
  departments: Array,
  fabrics: Array,
  colors: Array,
});

console.log('Props:', props);

const saving = ref(false);

const isInListById = (list, idKey, val) =>
  Array.isArray(list) && list.some(i => String(i[idKey]) === String(val));

const form = useForm({
  _method: 'POST',
  vid: props.sample.vid != null && props.sample.vid !== '' ? Number(props.sample.vid) : null,
  client: props.sample.client != null && props.sample.client !== '' ? Number(props.sample.client) : null,
  sample_type_id: props.sample.sample_type_id ?? null,

  department: props.sample.department ?? "",
  fabric: props.sample.fabric ?? "",

  sample_id_val: props.sample.sample_id_val ?? null,
  style_number: props.sample.style_number ?? null,
  size_field: props.sample.size_field ?? null,
  garment: props.sample.garment ?? null,
  detail_description: props.sample.detail_description ?? null,
  color: props.sample.color ?? null,
  fabric_code: props.sample.fabric_code ?? null,
  embroidery_new: props.sample.embroidery_new ?? false,
  samplecost: props.sample.samplecost ?? null,
  retailprice: props.sample.retailprice ?? null,
  conveyor: props.sample.conveyor ?? null,
  slot: props.sample.slot ?? null,
  notes: props.sample.notes ?? null,
  location: props.sample.location ?? null,
  atclient: props.sample.atclient ?? null,
  imagesFiles: [],
});

watch(() => form.conveyor, (val) => {
  if (val === '' || val === 0 || val === '0' || val === undefined) {
    form.conveyor = null;
  }
  if (!form.conveyor) {
    form.slot = null;
  }
});

const hasImagesRef = ref(Boolean(props.sample?.image_media?.length));

const onImagesPresent = (val) => {
  hasImagesRef.value = !!val;
  if (val) form.clearErrors('images');
};

function onCreateImagesSelected(e) {
  const files = Array.from(e.target.files || []);
  form.imagesFiles = files;
  if (files.length > 0) {
    form.clearErrors('images');
  }
}

const saveForm = () => {

  // Frontend-only required validations
  const requiredFields = [
    { key: 'garment', label: 'Garment', el: 'garment' },
    { key: 'detail_description', label: 'Garment Description', el: 'detail_description' },
    { key: 'color', label: 'Color', el: 'color' },
    { key: 'department', label: 'Department', el: 'department' },
    { key: 'location', label: 'Location', el: 'location' },
    { key: 'fabric', label: 'Fabric', el: 'fabric' },
  ];

  if (isConveyorLocation.value) {
    requiredFields.push(
      { key: 'conveyor', label: 'Conveyor', el: 'conveyor' },
      { key: 'slot', label: 'Slot', el: 'slot' },
    );
  }

  requiredFields.forEach(f => form.clearErrors(f.key));

  let firstInvalidElId = null;
  requiredFields.forEach(({ key, label, el }) => {
    const value = form[key];
    const isMissing = value === null || value === undefined || value === '';
    if (isMissing) {
      form.setError(key, `${label} is required.`);
      if (!firstInvalidElId) firstInvalidElId = el;
    }
  });

  if (firstInvalidElId) {
    const target = document.getElementById(firstInvalidElId);
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return;
  }

  if (props.sample.sample_id && !hasImagesRef.value) {
    form.setError('images', 'You should upload at least one image.');
    return;
  }

  console.log('Form before validation:', JSON.parse(JSON.stringify(form)));
  saving.value = true;
  let endpoint = route('admin.samples.store');
  if (props.sample.sample_id) {
    endpoint = route('admin.samples.update', { sample: props.sample.sample_id });
    form._method = 'PUT';
  }

  const element = document.getElementById('client');
  form.post(endpoint, {
    preserveScroll: true,
    forceFormData: true,
    onError() {
      element?.scrollIntoView();
    },
    onFinish() {
      saving.value = false;
    }
  });
};

const goBack = () => {
  if (typeof history !== 'undefined' && history.length > 1) {
    history.back();
  } else {
    let params = {};
    try {
      const f = localStorage.getItem('samples.filters');
      const s = localStorage.getItem('samples.sorting');
      if (f) params.filters = JSON.parse(f);
      if (s) params.sorting = JSON.parse(s);
    } catch (e) { }
    Inertia.get(route('admin.samples.index'), params, { preserveState: true, preserveScroll: true });
  }
};

const clientOptions = computed(() =>
  (props.clients || [])
    .filter((client) => client?.client && (client?.ID != null || client?.id != null))
    .map((client) => ({
      label: client.client,
      value: Number(client.ID ?? client.id),
    }))
);

const vendorOptions = computed(() =>
  (props.vendors || [])
    .filter(v => v?.vendorName && v?.vendorID != null)
    .map(v => ({ label: v.vendorName, value: Number(v.vendorID) }))
);

const clientModel = computed({
  get: () => (form.client != null && form.client !== "" ? Number(form.client) : null),
  set: (v) => { form.client = v == null ? "" : Number(v); },
});

const vendorModel = computed({
  get: () => (form.vid != null && form.vid !== "" ? Number(form.vid) : null),
  set: (v) => { form.vid = v == null ? "" : Number(v); },
});

const uploadingTemp = ref(false);

const tempImages = computed(() => {
  return (form.imagesFiles || []).map((f, idx) => ({
    idx,
    name: f.name,
    url: URL.createObjectURL(f),
  }));
});

function removeTempImage(idx) {
  form.imagesFiles.splice(idx, 1);
}

const conveyorsSafe = computed(() => ({
  by_location: props.conveyors?.by_location ?? {},
  legacy: props.conveyors?.legacy ?? null,
}));

const isLegacyConveyor = computed(() => {
  const legacy = conveyorsSafe.value.legacy;
  if (!legacy) return false;

  return String(form.conveyor ?? '') === String(legacy.value);
});

const selectedLocation = computed(() =>
  (props.locations || []).find(
    (loc) => String(loc.locationId) === String(form.location)
  ) || null
);

const isConveyorLocation = computed(() => {
  const loc = selectedLocation.value;
  return !!(loc && loc.is_conveyor);
});

const conveyorsForLocation = computed(() => {
  const locId = form.location;
  if (!locId) return [];

  const map = conveyorsSafe.value.by_location || {};
  const key = String(locId);

  return map[key] || [];
});

watch(
  () => form.location,
  () => {
    if (!isConveyorLocation.value) {
      form.conveyor = null;
      form.slot = null;
    }
  }
);
</script>

<template>
  <AppLayout title="Edit Database Sample">
    <template #title>
      <a href="#" @click.prevent="goBack">
        <SecondaryButton>Back</SecondaryButton>
      </a>
    </template>

    <div class="max-w-7xl mx-auto py-5 sm:px-6 lg:px-8">
      <FormSection>
        <template #title>
          <a href="#" @click.prevent="goBack">
            <SecondaryButton>Back</SecondaryButton>
          </a>
        </template>

        <template #form>
          <!-- Client -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Client" id="client" :required="true" />
            <SearchSelect v-model="clientModel" :options="clientOptions" placeholder="Select a client" />
            <InputError :message="form.errors.client" class="mt-2" />
          </div>

          <!-- Department -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Department" :required="true" />
            <SelectInput
              class="mt-1"
              v-model="form.department" id="department">
              <option v-if="!form.department" disabled value="">
                Select a department
              </option>

              <option v-if="form.department && !isInListById(departments, 'id', form.department)"
                :value="form.department">
                {{ form.department }}
              </option>

              <option v-for="dep in departments" :key="dep.id" :value="String(dep.id)">
                {{ dep.department }}
              </option>
            </SelectInput>
            <InputError :message="form.errors.department" class="mt-2" />
          </div>

          <!-- Type of samples -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Type of samples" />
            <SelectInput name="sample_type_id"
              class="mt-1"
              v-model="form.sample_type_id" id="sample_type_id">
              <option v-if="!form.sample_type_id" disabled value="">
                Select type of sample
              </option>
              <option v-for="sampleType in sampleTypes" :key="sampleType.type_id" :value="sampleType.type_id">
                {{ sampleType.sample_type }}
              </option>
            </SelectInput>
            <InputError :message="form.errors.sample_type_id" class="mt-2" />
          </div>

          <!-- Sample Name -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Sample Name" :required="true" />
            <TextInput id="sample_id_val" v-model="form.sample_id_val" type="text" class="mt-1 block w-full" />
            <InputError :message="form.errors.sample_id_val" class="mt-2" />
          </div>

          <!-- Vendor -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Vendor" />
            <SearchSelect v-model="vendorModel" :options="vendorOptions" placeholder="Select a vendor" />
            <InputError :message="form.errors.vid" class="mt-2" />
          </div>

          <!-- Style Number -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Style Number" />
            <TextInput id="style_number" v-model="form.style_number" type="text" class="mt-1 block w-full" />
            <InputError :message="form.errors.style_number" class="mt-2" />
          </div>

          <!-- Size -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Size" />
            <TextInput id="size_field" v-model="form.size_field" type="text" class="mt-1 block w-full" />
            <InputError :message="form.errors.size_field" class="mt-2" />
          </div>

          <!-- Garment -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Garment" :required="true" />
            <SelectInput name="garment"
              class="mt-1"
              v-model="form.garment" id="garment">
              <option v-if="!form.garment" disabled value="">
                Select a garment
              </option>
              <option v-for="g in garments" :key="g.garmentID" :value="g.garmentID">
                {{ g.garmentName }}
              </option>
            </SelectInput>
            <InputError :message="form.errors.garment" class="mt-2" />
          </div>

          <!-- Description -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Garment Description" :required="true" />
            <textarea rows="10" id="detail_description" v-model="form.detail_description"
              class="border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm mt-1 block w-full disabled:bg-gray-50"></textarea>
            <InputError :message="form.errors.detail_description" class="mt-2" />
          </div>

          <!-- Color -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Color" :required="true" />
            <SelectInput
              class="mt-1"
              v-model="form.color" id="color">
              <option v-if="!form.color" disabled value="">
                Select a color
              </option>

              <option v-if="form.color && !isInListById(colors, 'colorID', form.color)" :value="form.color">
                {{ form.color }}
              </option>

              <option v-for="c in colors" :key="c.colorID" :value="String(c.colorID)">
                {{ c.colorName }}
              </option>
            </SelectInput>
            <InputError :message="form.errors.color" class="mt-2" />
          </div>

          <!-- Fabric -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Fabric" :required="true" />
            <SelectInput
              class="mt-1"
              v-model="form.fabric" id="fabric">
              <option v-if="!form.fabric" disabled value="">
                Select a fabric
              </option>

              <option v-if="form.fabric && !isInListById(fabrics, 'fabricID', form.fabric)" :value="form.fabric">
                {{ form.fabric }}
              </option>

              <option v-for="fab in fabrics" :key="fab.fabricID" :value="String(fab.fabricID)">
                {{ fab.fabName }}
              </option>
            </SelectInput>
            <InputError :message="form.errors.fabric" class="mt-2" />
          </div>

          <!-- Fabric Code -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Fabric Code" />
            <TextInput id="fabric_code" v-model="form.fabric_code" type="text" class="mt-1 block w-full" />
            <InputError :message="form.errors.fabric_code" class="mt-2" />
          </div>

          <!-- Embroidery/Silk Screening -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Embroidery/Silk Screening" />
            <label class="mr-5">
              <input type="radio" v-model="form.embroidery_new" :value="true" id="embroideryYes" />
              <span class="ml-1">Yes</span>
            </label>
            <label class="mr-5">
              <input type="radio" v-model="form.embroidery_new" :value="false" id="embroideryNo" />
              <span class="ml-1">No</span>
            </label>
          </div>

          <!-- Cost -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Cost" />
            <TextInput id="samplecost" v-model="form.samplecost" type="text" class="mt-1 block w-full" />
            <InputError :message="form.errors.samplecost" class="mt-2" />
          </div>

          <!-- Retail Price -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Retail Price" />
            <TextInput id="retailprice" v-model="form.retailprice" type="text" class="mt-1 block w-full" />
            <InputError :message="form.errors.retailprice" class="mt-2" />
          </div>

          <!-- Location -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Location" :required="true" />
            <SelectInput name="location"
              class="mt-1"
              v-model="form.location" id="location">
              <option v-if="!form.location" disabled value="">
                Select a location
              </option>
              <option v-for="location in locations" :key="location.locationId" :value="location.locationId">
                {{ location.name }}
              </option>
            </SelectInput>
            <InputError :message="form.errors.location" class="mt-2" />
          </div>

          <!-- Conveyor -->
          <div class="col-span-6 sm:col-span-4" v-if="isConveyorLocation">
            <InputLabel value="Conveyor" :required="true" />
            <SelectInput name="conveyor" id="conveyor" v-model="form.conveyor"
              class="mt-1">
              <option v-if="isLegacyConveyor && conveyorsSafe.legacy" :value="conveyorsSafe.legacy.value" disabled>
                {{ conveyorsSafe.legacy.label }}
              </option>
              <option v-else disabled value="">
                Select a conveyor
              </option>
              <option v-for="c in conveyorsForLocation" :key="c.id" :value="c.id">
                {{ c.label }}
              </option>
            </SelectInput>
            <InputError :message="form.errors.conveyor" class="mt-2" />
          </div>

          <!-- Slot -->
          <div class="col-span-6 sm:col-span-4" v-if="isConveyorLocation">
            <InputLabel value="Slot" :required="true" />
            <TextInput id="slot" v-model="form.slot" type="text" class="mt-1 block w-full" />
            <InputError :message="form.errors.slot" class="mt-2" />
          </div>

          <!-- Notes -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Notes" />
            <textarea id="notes" v-model="form.notes"
              class="border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm mt-1 block w-full disabled:bg-gray-50"></textarea>
            <InputError :message="form.errors.notes" class="mt-2" />
          </div>
        </template>
      </FormSection>

      <br>

      <FormSection :noGrid="true" v-if="!sample.sample_id">
        <template #title>Images</template>

        <template #form>
          <div id="images-create" class="space-y-3">
            <InputLabel value="Upload images" :required="true" />

            <div class="flex justify-center items-center w-full">
              <label for="images-create-input"
                class="flex flex-col justify-center items-center w-full h-32 rounded-lg border-2 border-dashed cursor-pointer hover:bg-gray-100"
                :class="uploadingTemp ? 'border-gray-500 bg-gray-100' : 'border-gray-300'">
                <div class="flex flex-col justify-center items-center pt-5 pb-6">
                  <svg aria-hidden="true" class="mb-3 w-10 h-10 text-gray-400" fill="none" stroke="currentColor"
                    viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                  </svg>
                  <p class="mb-2 text-sm text-gray-500">
                    <span class="font-semibold">Click to upload</span> or drag and drop
                  </p>
                  <p class="text-xs text-gray-500">Max. File Size: 10MB</p>
                </div>

                <input id="images-create-input" type="file" accept="image/*" multiple @change="onCreateImagesSelected"
                  class="hidden" />
              </label>
            </div>

            <div class="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4" v-if="tempImages.length">
              <div v-for="img in tempImages" :key="img.idx" class="relative rounded-lg p-2 bg-white shadow-sm">
                <img :src="img.url" alt="" class="w-full h-32 object-cover rounded-md" />
                <div class="mt-1 text-xs truncate text-center">{{ img.name }}</div>
                <button type="button" class="absolute top-1 right-1 bg-white/80 rounded-full p-1 hover:bg-white"
                  @click="removeTempImage(img.idx)" title="Remove image">
                  <span class="fa fa-trash text-red-600 text-sm"></span>
                </button>
              </div>
            </div>

            <InputError :message="form.errors.images" class="mt-2" />
          </div>
        </template>
      </FormSection>

      <FormSection :noGrid="true" v-if="sample.sample_id">
        <template #title>
          Images and Files
        </template>
        <template #form>
          <SampleDatabaseUpload :media-input="{ 'I': sample.image_media, 'F': sample.file_media }"
            :sample-id="sample.sample_id" :required="true" @images-present="onImagesPresent" />
          <InputError :message="form.errors.images" class="mt-2" />
        </template>
      </FormSection>

      <br>

      <FormSection :noGrid="true">
        <template #actions>
          <a href="#" class="mr-5" v-if="!saving" @click.prevent="goBack">
            <SecondaryButton type="button">Back</SecondaryButton>
          </a>
          <PrimaryButton type="button" @click.prevent="saveForm" :disabled="saving">Save</PrimaryButton>
        </template>
      </FormSection>
    </div>
  </AppLayout>
</template>
